import React, { useState } from 'react';
import PricingCard from './PricingCard';
import { TIERS, SOONER, formatPrice } from '../../content/tiers';
import { useAuth } from '../../context/AuthContext';
import useOrdering from '../../hooks/useOrdering';

/**
 * Where each card's "Get started" goes.
 *
 * Make Your Own: signed-in visitors go straight to a new video, everyone else to
 * sign-up first. The team-made options (We Mix It For You, Restored & Remastered)
 * work the same way once online team ordering is switched on (useOrdering — on when
 * SMS is configured on the server). Until then they open the contact form on
 * "Support / Book a Call", phone required, with a short starting message.
 * See docs/internal-guides/operations/PRICING_AND_TIERS_OPERATOR_MANUAL.md.
 */
export const actionFor = (tier, isSignedIn, teamOrdering = false) => {
  if (tier.orderable || (tier.teamBuilt && teamOrdering)) {
    // Signing up first: the choice rides along in ?next=, so the new video starts
    // with the option they picked here instead of asking again.
    if (!isSignedIn) return { to: `/register?next=${encodeURIComponent(`/projects/new?tier=${tier.id}`)}`, state: { tier: tier.id } };
    return { to: '/projects/new', state: { tier: tier.id } };
  }
  return {
    to: '/contact',
    state: {
      type: 'support',
      // Team-made videos need a phone number so the team can arrange the order.
      contactMethod: 'phone',
      message: `I'd like ${tier.name} (${formatPrice(tier.priceCents)}). Please get in touch about next steps.`,
    },
  };
};

/**
 * @param inApp  true inside the signed-in app (the "+ New Video" chooser, which sits
 *               behind PrivateRoute): Make Your Own always goes straight to a new video
 *               rather than to sign-up. Guests never reach the chooser — PrivateRoute
 *               sends them to sign-up first, the same rule as /projects/new.
 */
const PricingSection = ({ id = 'pricing', className = '', inApp = false }) => {
  // Inside the app each card starts a video of that kind straight away.
  const ctaLabel = inApp ? 'Start new project' : 'Get started';
  const { isAuthenticated, isGuest } = useAuth();
  const signedIn = inApp || (isAuthenticated && !isGuest);
  const { teamOrdering } = useOrdering();
  // One shared state: opening "How it works" on any card opens it on all three, so
  // the options can be compared step by step side by side.
  const [howOpen, setHowOpen] = useState(false);

  return (
    <div id={id} className={className}>
      <div className="grid gap-8 md:grid-cols-3 max-w-[1080px] mx-auto items-stretch">
        {TIERS.map((tier) => (
          <PricingCard
            key={tier.id}
            tier={tier}
            action={actionFor(tier, signedIn, teamOrdering)}
            open={howOpen}
            onToggle={() => setHowOpen((o) => !o)}
            ctaLabel={ctaLabel}
          />
        ))}
      </div>

      {/* One shared line under all three cards, never repeated per card. */}
      <p className="mt-10 text-center text-brand-ink text-base">
        {SOONER.text.replace(' Call us.', ' ')}
        <a
          href={SOONER.phoneHref}
          className="font-bold underline underline-offset-4 decoration-1 hover:text-brand-coral-deep"
        >
          Call us<span className="sr-only"> on {SOONER.phoneDisplay}</span>.
        </a>
      </p>
    </div>
  );
};

export default PricingSection;
